// @spec SHL-STR-017
// @spec SHL-STR-018
// API client functions for platform-wide search

import type { ApiClient } from '@/lib/api-client';

/**
 * Date range filter options.
 */
export type DateRangeFilter = 'week' | 'month' | 'year';

/**
 * Optional metadata for search results.
 */
export interface SearchResultMetadata {
  role?: string | null;
  avatarUrl?: string | null;
  memberCount?: number | null;
}

/**
 * Individual search result item.
 */
export interface SearchResult {
  /** Unique identifier of the result */
  id: string;
  /** Primary display text */
  title: string;
  /** Secondary text (email, description) */
  subtitle: string | null;
  /** Navigation URL for this result */
  url: string;
  /** Lucide icon name for this result */
  icon: string;
  /** Additional result-specific data */
  metadata: SearchResultMetadata;
}

/**
 * Category grouping for search results.
 */
export interface SearchCategory {
  /** Category identifier (e.g., 'members') */
  name: string;
  /** Human-readable category name */
  displayName: string;
  /** Lucide icon name for category */
  icon: string;
  /** Total results in this category */
  count: number;
  /** Results in this category */
  results: SearchResult[];
}

/**
 * Available filter options returned from API.
 */
export interface AvailableFilters {
  /** Available category names */
  categories: string[];
  /** Available date range options */
  dateRanges: DateRangeFilter[];
}

/**
 * Currently applied filters.
 */
export interface AppliedFilters {
  /** Applied category filters */
  categories: string[];
  /** Applied date range filter */
  dateRange: DateRangeFilter | null;
}

/**
 * Response schema for the search endpoint.
 */
export interface SearchResponse {
  /** The search query that was executed */
  query: string;
  /** Total number of results across all categories */
  totalResults: number;
  /** Results grouped by category */
  categories: SearchCategory[];
  /** Available filter options */
  availableFilters: AvailableFilters;
  /** Currently applied filters */
  appliedFilters: AppliedFilters;
}

/**
 * Parameters for the search API call.
 */
export interface SearchParams {
  /** Search query (min 2 chars) */
  q: string;
  /** Results per category (default: 5, max: 10) */
  limit?: number;
  /** Filter by category names */
  categories?: string[];
  /** Filter by date range */
  dateRange?: DateRangeFilter;
}

/**
 * Search across platform entities.
 *
 * Searches users and organizations visible to the current user.
 * Results are grouped by category with limited results per category.
 *
 * @param apiClient - Configured API client with auth token
 * @param params - Search parameters
 * @returns Search results grouped by category
 *
 * @example
 * const { getToken } = useAuth();
 * const client = createApiClient({ getAuthToken: getToken });
 * const results = await searchPlatform(client, { q: 'john', limit: 5 });
 */
export async function searchPlatform(
  apiClient: ApiClient,
  params: SearchParams
): Promise<SearchResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('q', params.q);
  if (params.limit !== undefined) {
    searchParams.set('limit', String(params.limit));
  }
  if (params.categories && params.categories.length > 0) {
    searchParams.set('categories', params.categories.join(','));
  }
  if (params.dateRange) {
    searchParams.set('date_range', params.dateRange);
  }

  return apiClient.get<SearchResponse>(`/search?${searchParams.toString()}`);
}
